import React, { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { CreatableAsyncSelectWrapper, PrimarySelect, CircularLoaderSm } from 'styles/components'
import { Wrapper, FloatingContainer, Label, Input } from './styles'
import { withTheme } from 'styled-components'

const PrimaryInputWrapper = (props) => {
  const {
    placeholder,
    height,
    width,
    onChange,
    ref,
    isMulti,
    label,
    required,
    theme,
    value,
    loading,
    creatable,
    onCreate,
    loadOptions,
    defaultOptions,
    onBlur,
    ...rest
  } = props
  const [isOpen, setIsOpen] = useState(false)
  const [selectedOption, setSelectedOption] = useState(value)
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 })
  const selectRef = useRef(useRef)

  useEffect(() => {
    setSelectedOption(value)
  }, [value])

  const calculatePosition = () => {
    if (selectRef.current) {
      const rect = selectRef.current.getBoundingClientRect()
      const viewportWidth = window.innerWidth
      const dropdownWidth = Math.max(rect.width, 240)

      // Position directly below the input using getBoundingClientRect
      // This gives us the position relative to the viewport, accounting for all scroll containers
      const finalTop = rect.bottom + 1 // 1px gap
      const finalLeft = rect.left

      // Ensure dropdown stays within viewport bounds
      let adjustedLeft = finalLeft
      if (finalLeft + dropdownWidth > viewportWidth) {
        adjustedLeft = viewportWidth - dropdownWidth - 4
      }
      if (adjustedLeft < 4) {
        adjustedLeft = 4
      }

      const position = {
        top: finalTop,
        left: adjustedLeft,
        width: Math.min(dropdownWidth, viewportWidth - 8),
      }

      setPosition(position)
    }
  }

  const selectStyles = {
    control: (provided) => ({ ...provided, minWidth: 240, margin: 0 }),
    menu: () => ({
      boxShadow: 'inset 0 1px 0 rgba(0, 0, 0, 0.1)',
      backgroundColor: theme.colors.principalWhite,
    }),
  }

  useEffect(() => {
    function clickOutside(event) {
      // Check if click is outside both the select input and the portal dropdown
      const isOutsideSelect = selectRef.current && !selectRef.current.contains(event.target)
      const isOutsideDropdown = !event.target.closest('[data-dropdown-portal]')

      if (isOutsideSelect && isOutsideDropdown) {
        setIsOpen(false)
        if (onBlur) onBlur()
      }
    }

    function handleScroll() {
      if (isOpen) {
        calculatePosition()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', clickOutside)
      window.addEventListener('scroll', handleScroll, true)
      window.addEventListener('resize', handleScroll)
    }

    return () => {
      document.removeEventListener('mousedown', clickOutside)
      window.removeEventListener('scroll', handleScroll, true)
      window.removeEventListener('resize', handleScroll)
    }
  }, [selectRef, isOpen, onBlur])

  return (
    <Wrapper
      name={label}
      ref={selectRef}
      isOpen={isOpen}
      height={height}
      width={width}
      selectedOption={selectedOption}
      controlShouldRenderValue={false}
      hideSelectedOptions={false}
      isClearable={false}
      isLoading={loading}
      onClick={
        loading
          ? () => {}
          : () => {
              if (!isOpen) {
                // Small delay to ensure DOM is ready
                setTimeout(() => {
                  calculatePosition()
                }, 10)
              }
              setIsOpen(!isOpen)
            }
      }
    >
      {loading && (
        <div style={{ position: 'absolute', right: 5, bottom: 7 }}>
          <CircularLoaderSm></CircularLoaderSm>
        </div>
      )}
      {label && <Label>{label}</Label>}
      {!isMulti && (
        <Input
          required={required}
          placeholder={placeholder}
          value={value ? value.label : ''}
          onClick={() => {
            // Small delay to ensure DOM is ready
            setTimeout(() => {
              calculatePosition()
            }, 10)
            setIsOpen(true)
          }}
        />
      )}
      {isMulti && (
        <Input
          required={required}
          placeholder={placeholder}
          value={value ? value.map((v) => v.label).join(', ') : ''}
          onClick={() => {
            // Small delay to ensure DOM is ready
            setTimeout(() => {
              calculatePosition()
            }, 10)
            setIsOpen(true)
          }}
        />
      )}
      {isOpen &&
        createPortal(
          <FloatingContainer
            data-dropdown-portal
            style={{
              position: 'fixed',
              top: position.top,
              left: position.left,
              width: position.width,
              zIndex: 99999,
              // maxHeight: '300px',
              // overflowY: 'auto'
            }}
          >
            {!creatable && (
              <PrimarySelect
                autoFocus
                backspaceRemovesValue={false}
                menuIsOpen
                controlShouldRenderValue={false}
                hideSelectedOptions={false}
                {...rest}
                isMulti={isMulti}
                ref={ref}
                value={value}
                label={label}
                styles={selectStyles}
                placeholder={placeholder}
                tabSelectsValue={false}
                onChange={(option) => {
                  setSelectedOption(option)
                  onChange(option)
                  setIsOpen(false)
                  if (onBlur) onBlur()
                }}
              />
            )}
            {creatable && (
              <CreatableAsyncSelectWrapper
                autoFocus
                backspaceRemovesValue={false}
                menuIsOpen
                controlShouldRenderValue={false}
                hideSelectedOptions={false}
                // {...rest}
                isMulti={isMulti}
                ref={ref}
                value={value}
                label={label}
                isLoading={loading}
                formatCreateLabel={(option) => `Criar ${option}`}
                styles={selectStyles}
                placeholder={placeholder}
                onCreateOption={onCreate}
                tabSelectsValue={false}
                loadOptions={loadOptions}
                defaultOptions={defaultOptions}
                onChange={(option) => {
                  setSelectedOption(option)
                  onChange(option)
                  setIsOpen(false)
                  if (onBlur) onBlur()
                }}
              />
            )}
          </FloatingContainer>,
          document.body
        )}
    </Wrapper>
  )
}

export default withTheme(PrimaryInputWrapper)
